import React, { Component, Fragment } from 'react';
import { Cell, Row as TableRow, StickyTable } from 'react-sticky-table';
import MASpinner from '../common/MASpinner';
import { niceDateFormatter, userNameFormatter } from '../../lib/helper/formatting';
import { isSeniorStaff, isStaff } from '../../lib/helper/authorisation';
import { FormCheck } from 'react-bootstrap';
import { renderField } from '../../lib/helper/form';
import { change, Field, Form, getFormValues, reduxForm, reset } from 'redux-form';
import { onboardingItemsListPatch } from '../../lib/redux/actions/onboardingItems';
import { connect } from 'react-redux';
import { addToast } from '../../lib/redux/actions/toast';
import Card from 'react-bootstrap/Card';
import Alert from 'react-bootstrap/Alert';

export const getChangedFormRowValues = (formValues = {}, initialValues = {}) => {
  const changedIds = new Set();
  const result = {};

  Object.keys(formValues).forEach((key) => {
    if (formValues[key] !== initialValues[key]) {
      changedIds.add(key.split('-')[1]);
    }
  });

  Object.keys(formValues).forEach((key) => {
    if (changedIds.has(key.split('-')[1])) {
      result[key] = formValues[key];
    }
  });

  return result;
};

const mapDispatchToProps = {
  onboardingItemsListPatch,
  addToast,
};

const mapStateToProps = (state, props) => {
  let initialValues = {};
  if (props.onboardingItems) {
    props.onboardingItems.forEach((obi) => {
      initialValues['isComplete-' + obi.id] = obi.isComplete;
      initialValues['notes-' + obi.id] = obi.notes ? obi.notes : '';
    });
  }
  return {
    currentFormValues: getFormValues('consultantOnboardingForm')(state),
    initialValues: initialValues,
  };
};
class ConsultantOnboardingItemsTable extends Component {
  buildFormData = (formValues) => {
    let grouped = {};
    Object.keys(formValues).forEach((key) => {
      let [name, id] = key.split('-');
      if (!grouped[id]) {
        grouped[id] = { id };
      }
      grouped[id][name] = formValues[key];
    });
    return Object.values(grouped);
  };

  onSubmit = (formValues) => {
    const { initialValues } = this.props;
    const changedValues = getChangedFormRowValues(formValues, initialValues);

    let formData = this.buildFormData(changedValues);
    return this.props.onboardingItemsListPatch(formData).then(() => {
      this.props.addToast('Onboarding Items successfully saved.', true);
    });
  };

  handleCompleteCheck = (event) => {
    this.props.dispatch(change('consultantOnboardingForm', event.target.id, event.target.checked));
  };

  renderCompleteCheck = (onboardingItem) => {
    if (onboardingItem.isComplete) {
      if (isSeniorStaff(false)) {
        return (
          <FormCheck
            type="switch"
            id={`isComplete-${onboardingItem.id}`}
            name={`isComplete-${onboardingItem.id}`}
            checked={
              this.props.currentFormValues && typeof this.props.currentFormValues[`isComplete-${onboardingItem.id}`] !== 'undefined'
                ? this.props.currentFormValues[`isComplete-${onboardingItem.id}`]
                : false
            }
            onChange={(e) => this.handleCompleteCheck(e)}
          />
        );
      }
      return <i className="fa fa-check-circle text-primary" />;
    } else if (isStaff(true) && !onboardingItem.isComplete) {
      return <i className="fa fa-times-circle text-danger" />;
    } else {
      return (
        <FormCheck
          type="switch"
          id={`isComplete-${onboardingItem.id}`}
          name={`isComplete-${onboardingItem.id}`}
          checked={
            this.props.currentFormValues && typeof this.props.currentFormValues[`isComplete-${onboardingItem.id}`] !== 'undefined'
              ? this.props.currentFormValues[`isComplete-${onboardingItem.id}`]
              : false
          }
          onChange={(e) => this.handleCompleteCheck(e)}
        />
      );
    }
  };

  render() {
    const { isFetchingOnboardingItems, onboardingItems, currentFormValues, type, pristine } = this.props;
    return (
      <Fragment>
        {this.props.consultant.id <= window.CONSULTANT_PRE_ONBOARDING_ID && (
          <Card.Body>
            <Alert variant={'info'} className="mb-0">
              This consultant was created prior to the onboarding functionality being added
            </Alert>
          </Card.Body>
        )}
        <div className="crud__section mb-3">
          <div className="crud__section-icon">
            <i className="fas fa-list-alt" />
          </div>
          {isFetchingOnboardingItems && <MASpinner wrapperClasses={''} />}
          <h2 className="crud__section-header">
            {type === 'onboarding' ? 'Onboarding' : 'Leaving'} Checklist
            <span className="ml-4 badge badge-info">
              ({onboardingItems.filter((i) => i.isComplete).filter((i) => i.onboardingItemTemplate.type === type).length}/
              {onboardingItems.filter((i) => i.onboardingItemTemplate.type === type).length} Complete)
            </span>
          </h2>
          <Form onSubmit={this.props.handleSubmit(this.onSubmit)}>
            <div style={{ minWidth: '100%' }}>
              <StickyTable>
                <TableRow className="font-weight-bold">
                  <Cell className="sticky-table-cell border-right">Category</Cell>
                  <Cell className="sticky-table-cell border-right">Description</Cell>
                  <Cell className="sticky-table-cell border-right">Assignee Initials</Cell>
                  <Cell className="sticky-table-cell border-right">Notes</Cell>
                  <Cell className="sticky-table-cell border-right">Is Complete</Cell>
                  <Cell className="sticky-table-cell border-right">Date Completed</Cell>
                  <Cell className="sticky-table-cell border-right">Completed By</Cell>
                </TableRow>
                {onboardingItems
                  .filter((i) => i.onboardingItemTemplate.type === type)
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((onboardingItem) => {
                    const formIsComplete = currentFormValues?.[`isComplete-${onboardingItem.id}`] ?? onboardingItem.isComplete;
                    const notesDirty = (currentFormValues?.[`notes-${onboardingItem.id}`] ?? '') !== (onboardingItem.notes ?? '');
                    const showNoteField = isSeniorStaff() && (!formIsComplete || notesDirty);
                    return (
                      <TableRow key={onboardingItem.id}>
                        <Cell className="border-right">{onboardingItem.onboardingItemTemplate?.category ?? 'General'}</Cell>
                        <Cell className="border-right">{onboardingItem.description ?? '-'}</Cell>
                        <Cell className="border-right">{onboardingItem.assigneeInitials ?? '-'}</Cell>
                        <Cell className="border-right">
                          {!showNoteField ? (
                            <div>{onboardingItem.notes}</div>
                          ) : (
                            <Field
                              name={`notes-${onboardingItem.id}`}
                              id={`notes-${onboardingItem.id}`}
                              rows="3"
                              type="textarea"
                              formLabelClasses={'d-none'}
                              groupClasses={'mb-0'}
                              className={'mt-n2 mb-n2'}
                              component={renderField}
                              value={onboardingItem.notes}
                            />
                          )}
                        </Cell>
                        <Cell className="border-right text-center">{this.renderCompleteCheck(onboardingItem)}</Cell>
                        <Cell className="border-right">{onboardingItem.dateCompleted ? niceDateFormatter(onboardingItem.dateCompleted) : '-'}</Cell>
                        <Cell className="border-right">{onboardingItem.completingUser ? userNameFormatter(onboardingItem.completingUser) : '-'}</Cell>
                      </TableRow>
                    );
                  })}
              </StickyTable>
            </div>
            {isStaff(true) ? (
              <></>
            ) : (
              <button type="submit" disabled={pristine} className="btn btn-primary btn-block mt-4">
                Update
              </button>
            )}
          </Form>
        </div>
      </Fragment>
    );
  }
}

const afterSubmit = (result, dispatch) => dispatch(reset('consultantOnboardingForm'));

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: 'consultantOnboardingForm',
    enableReinitialize: true,
    onSubmitSuccess: afterSubmit,
  })(ConsultantOnboardingItemsTable),
);
